import { useNavigate } from 'react-router-dom'
import { motion } from 'motion/react'
import { Sparkles, ArrowRight, CheckCircle2 } from 'lucide-react'
import './HowItWorks.css'

const steps = [
  {
    number: 1,
    title: 'Start with a Story',
    description:
      "Every lesson begins with a real-life news story. This helps children understand why the topic matters and what's at stake.",
    image: '/assets/icons/mic.webp',
    imageAlt: 'Mirror icon',
    glowClass: 'hiw__glow--1',
    badgeClass: 'hiw__badge--1',
    iconHaloClass: 'hiw__icon-halo--1',
    iconSizeClass: '',
    badgeFloatAnimate: { y: [0, -4, 0] },
    badgeFloatTransition: { duration: 3, repeat: Infinity, ease: 'easeInOut' },
    iconFloat: {
      y: [0, -6, 0],
      rotate: [0, 2, 0, -2, 0],
      scale: [1, 1.01, 1]
    },
    iconFloatTransition: { duration: 6, repeat: Infinity, ease: 'easeInOut' },
    cardHover: { y: -12, scale: 1.02 },
    iconHover: { scale: 1.08, rotate: 6 },
    showSparkle: false
  },
  {
    number: 2,
    title: <>Crack the Case with the Digipalz</>,
    description:
      'Kids solve puzzles and outsmart villains, building real online safety skills while having a blast.',
    image: '/assets/icons/mirror.webp',
    imageAlt: 'Chat icon',
    glowClass: 'hiw__glow--2',
    badgeClass: 'hiw__badge--2',
    iconHaloClass: 'hiw__icon-halo--2',
    iconSizeClass: 'hiw__icon--2',
    badgeFloatAnimate: { y: [0, -5, 0] },
    badgeFloatTransition: {
      duration: 2.5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 0.5
    },
    iconFloat: {
      y: [0, -7, 0],
      rotate: [0, -3, 0, 3, 0],
      scale: [1, 1.02, 1]
    },
    iconFloatTransition: {
      duration: 5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1
    },
    cardHover: { y: -12, scale: 1.02 },
    iconHover: { scale: 1.08, rotate: -8 },
    showSparkle: false
  },
  {
    number: 3,
    title: 'Spark Discussion',
    description:
      'Conversation starters guide family discussions after each lesson — building trust and open dialogue so kids turn to you first when something online feels wrong.',
    image: '/assets/icons/chat.webp',
    imageAlt: 'Mic icon',
    glowClass: 'hiw__glow--3',
    badgeClass: 'hiw__badge--3',
    iconHaloClass: 'hiw__icon-halo--3',
    iconSizeClass: '',
    badgeFloatAnimate: { y: [0, -6, 0] },
    badgeFloatTransition: {
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1
    },
    iconFloat: {
      y: [0, -7, 0],
      rotate: [0, 4, 0, -4, 0],
      scale: [1, 1.02, 1]
    },
    iconFloatTransition: {
      duration: 7,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1.5
    },
    cardHover: { y: -12, scale: 1.02 },
    iconHover: { scale: 1.08, rotate: 10 },
    showSparkle: true
  }
]

const cardTransition = { duration: 0.5, ease: [0.16, 1, 0.3, 1] }

const HowItWorks = () => {
  const navigate = useNavigate()
  return (
    <div id='how-it-works' className='hiw'>
      <div className='hiw__bg-blur hiw__bg-blur--1' />
      <div className='hiw__bg-blur hiw__bg-blur--2' />

      <div className='hiw__inner'>
        <motion.div
          className='hiw__header'
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.h2 className='hiw__title'>How It Works</motion.h2>
          <motion.p className='hiw__subtitle'>
            Digipalz transforms cyber safety into interactive missions that
            build digital literacy and keep kids safe online.
          </motion.p>
        </motion.div>

        <div className='hiw__grid'>
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              className={`hiw__step-wrap hiw__step-wrap--${step.number}`}
              initial={{ opacity: 0, y: 80, scale: 0.8 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={{
                duration: 1,
                delay: 0.2 + index * 0.2,
                ease: [0.16, 1, 0.3, 1]
              }}
            >
              <div className={`hiw__glow ${step.glowClass}`} />

              <motion.div
                className='hiw__card'
                whileHover={step.cardHover}
                transition={cardTransition}
              >
                <motion.div
                  className='hiw__badge-wrap'
                  animate={step.badgeFloatAnimate}
                  transition={step.badgeFloatTransition}
                >
                  <div className={`hiw__badge ${step.badgeClass}`}>
                    {step.number}
                  </div>
                </motion.div>

                <motion.div
                  className='hiw__icon-wrap'
                  animate={step.iconFloat}
                  transition={step.iconFloatTransition}
                >
                  <div className='hiw__icon-inner'>
                    <div className={`hiw__icon-halo ${step.iconHaloClass}`} />
                    <motion.img
                      src={step.image}
                      alt={step.imageAlt}
                      className={`hiw__icon ${step.iconSizeClass}`}
                      whileHover={step.iconHover}
                      transition={{ duration: 0.3 }}
                      loading='lazy'
                      decoding='async'
                    />
                    {step.showSparkle && (
                      <motion.div
                        className='hiw__sparkle'
                        animate={{
                          rotate: [0, 360],
                          scale: [1, 1.2, 1]
                        }}
                        transition={{ duration: 3, repeat: Infinity }}
                      >
                        <Sparkles size={20} aria-hidden='true' />
                      </motion.div>
                    )}
                  </div>
                </motion.div>

                <h3 className='hiw__card-title'>{step.title}</h3>
                <p className='hiw__card-desc'>{step.description}</p>
              </motion.div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className='hiw__cta-wrap'
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <motion.button
            type='button'
            className='hiw__cta'
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/register')}
          >
            <Sparkles size={20} strokeWidth={2.5} aria-hidden='true' />
            START FREE TRIAL NOW
            <ArrowRight
              size={20}
              strokeWidth={2.5}
              className='hiw__cta-arrow'
              aria-hidden='true'
            />
          </motion.button>

          <div className='hiw__disclaimer'>
            <CheckCircle2 size={20} aria-hidden='true' />
            <span>7-day free trial • Cancel anytime</span>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default HowItWorks
